import React from 'react';
import { request } from './api/api';
import HttpMethods from '../enums/http-methods';
import { isIPC } from './service-lines';
import * as api from './api/api';
import * as commonEnums from '../enums/index';
import * as appServiceLines from './service-lines';



/**
 * Fetches the merged configuration jsonValue for a given configuration.
 * @param configurationId - The configuration ID.
 * @param tenantId - The tenant ID.
 * @param serviceLine - The service line.
 * @returns The merged configuration's jsonValue object, or {} if not found.
 */
export async function fetchMergedConfigurationJsonValue(
    configurationId: string,
    tenantId: string,
    serviceLine: string
): Promise<Record<string, any>> {
  if (!configurationId || !tenantId || !serviceLine) {
    console.warn(
        `[fetchMergedConfigurationJsonValue] Missing required parameters: configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${serviceLine}`
    );
    return {};
  }

  let effectiveServiceLine = serviceLine;
  if (isIPC(serviceLine)) {
    effectiveServiceLine = 'Inpatient Professional Coding';
  }

  const endpoint = `/configuration/detail/${configurationId}/${tenantId}/v2?merge=true&serviceLine=${effectiveServiceLine}`;
  const response = await request(endpoint, {
    method: HttpMethods.GET
  });

  const responseData = await response.json();

  if (!Array.isArray(responseData) || responseData.length === 0) {
    console.warn(
        `[fetchMergedConfigurationJsonValue] No configuration found for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
    );
    return {};
  }

  const configObj = responseData[0];
  if (!configObj || !Array.isArray(configObj.values)) {
    console.warn(
        `[fetchMergedConfigurationJsonValue] Config object missing or values not array for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
    );
    return {};
  }

  const mergedValueObj = configObj.values.find(
      (v: any) => v.value === 'MERGED'
  );

  if (!mergedValueObj || !mergedValueObj.jsonValue) {
    console.warn(
        `[fetchMergedConfigurationJsonValue] No merged value found for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
    );
    return {};
  }

  return mergedValueObj.jsonValue;
}


const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  redirect: 'follow',
  referrer: 'no-referrer'
};

export const getConfigurationsForSectionV2 = async (
    args: CMxCommonApp.SectionAPIArguments
): Promise<CMxAPI.ConfigurationSection[]> => {
  const searchParams = new URLSearchParams({});

  if (args?.tenantId) {
    searchParams.set('tenantId', args.tenantId);
  }

  let serviceLine = '*';
  if (args?.serviceLine) {
    serviceLine = appServiceLines.isIPC(args.serviceLine)
        ? 'Inpatient Professional Coding'
        : args.serviceLine;
  }

  searchParams.set('serviceLine', serviceLine);

  const result = await api.requestJSON<CMxAPI.ConfigurationSection[]>(
      `/configuration/section/${args.section}/v2?${searchParams.toString()}`,
      {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.GET
      }
  );

  return result;
};


export interface InvoiceFilteringConfig {
  coderConfig: CMxAPI.ConfigurationSection[];
  sourceInvoices: any[];
  activeItemArtifacts?: any[];
  tenantId: string;
  serviceLine: string;
}

export interface FilteredInvoicesResult {
  filteredSourceInvoices: any[];
  automateCheck: any;
  codeSourceDisplayInvoiceTypes: any;
  checkAutomateThreshold: boolean;
}

/**
 * Filters source invoices based on configuration settings and automation thresholds
 * @param config - Configuration object containing coderConfig, sourceInvoices, activeItemArtifacts, tenantId, and serviceLine
 * @returns Object containing filtered invoices and configuration values used for filtering
 */
export function filterSourceInvoices(config: InvoiceFilteringConfig): FilteredInvoicesResult {
  const { coderConfig, sourceInvoices, activeItemArtifacts, tenantId, serviceLine } = config;

  // Helper function to extract values from a configuration section by keyname
  const getValuesForKeyName = (keyName: commonEnums.ConfigurationKeyName): CMxAPI.ConfigurationValue[] => {
    const config = coderConfig.find(config => config.keyname === keyName);
    return config?.values || [];
  };

  // Get configuration values for each keyname
  const automateCheckValues = getValuesForKeyName(
      commonEnums.ConfigurationKeyName.AUTOMATE_CHECK_CODE_SOURCE_PREDICTED_INVOICE
  );

  const codeSourceDisplayInvoiceTypesValues = getValuesForKeyName(
      commonEnums.ConfigurationKeyName.CODE_SOURCE_DISPLAY_INVOICE_TYPES
  );

  // Use getConfigValueForServiceLine for sophisticated service line and tenant matching
  const automateCheck = getConfigValueForServiceLine(
      automateCheckValues,
      serviceLine,
      tenantId
  );

  const codeSourceDisplayInvoiceTypes = getConfigValueForServiceLine(
      codeSourceDisplayInvoiceTypesValues,
      serviceLine,
      tenantId
  );

  let filteredSourceInvoices = sourceInvoices;

  // Only filter if codeSourceDisplayInvoiceTypes is defined
  if (codeSourceDisplayInvoiceTypes && typeof codeSourceDisplayInvoiceTypes === 'object') {
    filteredSourceInvoices = filteredSourceInvoices.filter(
        (invoice: any) => {
          return codeSourceDisplayInvoiceTypes['code_source_invoice_types'].includes(invoice.type);
        });
  }

  const checkAutomateThreshold = (automateCheck && activeItemArtifacts?.[0]?.procedures?.automate) || false;

  if (checkAutomateThreshold) {
    filteredSourceInvoices = filteredSourceInvoices.filter(
        (invoice: any) => {
          return invoice.invoice_type !== commonEnums.InvoiceTypes.PREDICTION;
        }
    );
  }

  return {
    filteredSourceInvoices,
    automateCheck,
    codeSourceDisplayInvoiceTypes,
    checkAutomateThreshold
  };
}

export const serviceLineAliases: Record<string, string> = {
  'Bedside Pro': 'Inpatient Professional Coding'
};

// Checks if a configuration value's serviceLines match the given service line.
// The includeWildcard flag (default true) controls whether to count a wildcard match.
export function serviceLineMatches(
    configValue: CMxAPI.ConfigurationValue,
    serviceLineName: string,
    includeWildcard: boolean = true
): boolean {
  const normalizedServiceLine =
      serviceLineAliases[serviceLineName] || serviceLineName;
  return (
      configValue.serviceLines?.includes(serviceLineName) ||
      configValue.serviceLines?.includes(normalizedServiceLine) ||
      (includeWildcard && configValue.serviceLines?.includes('*'))
  );
}


// Add this new function to prioritize jsonValue over value
export function getConfigDisplayValue(
    configValue: CMxAPI.ConfigurationValue
): any {
  // If jsonValue exists and is not null/undefined, return it
  if (configValue.jsonValue !== null && configValue.jsonValue !== undefined) {
    return configValue.jsonValue;
  }

  // Fall back to the regular value
  return configValue.value;
}

// Retrieves configuration values for a given service line and tenant.
// It prioritizes an exact match (excluding wildcard) before falling back to wildcard matches.
// Now returns jsonValue if available, otherwise falls back to value.
export function getConfigValueForServiceLine(
    values: CMxAPI.ConfigurationValue[],
    serviceLineName: string,
    tenantId: string
): any {
  // Define match criteria in priority order (higher priority = lower index)
  const matchCriteria = [
    // 1. Exact tenant + Exact service line (highest priority)
    (v: CMxAPI.ConfigurationValue) =>
        v.tenantId === tenantId && serviceLineMatches(v, serviceLineName, false),

    // 2. Exact tenant + Wildcard service line
    (v: CMxAPI.ConfigurationValue) =>
        v.tenantId === tenantId && v.serviceLines?.includes('*'),

    // 3. Parent health system + Exact service line
    (v: CMxAPI.ConfigurationValue) =>
        v.tenantId !== tenantId && v.tenantId !== '*' && serviceLineMatches(v, serviceLineName, false),

    // 4. Parent health system + Wildcard service line
    (v: CMxAPI.ConfigurationValue) =>
        v.tenantId !== tenantId && v.tenantId !== '*' && v.serviceLines?.includes('*'),

    // 5. Wildcard tenant + Exact service line
    (v: CMxAPI.ConfigurationValue) =>
        v.tenantId === '*' && serviceLineMatches(v, serviceLineName, false),

    // 6. Wildcard tenant + Wildcard service line (lowest priority)
    (v: CMxAPI.ConfigurationValue) =>
        v.tenantId === '*' && v.serviceLines?.includes('*')
  ];

  // Find the first matching criteria that has results
  for (const criteria of matchCriteria) {
    const matches = values.filter(criteria);
    if (matches.length > 0) {
      return matches.length === 1
          ? getConfigDisplayValue(matches[0])
          : matches.map(v => getConfigDisplayValue(v));
    }
  }

  return null;
}
